import { useCallback, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DragAndDropZone } from '@odo/shared-ui';
import { useUploadPracticeDocument } from '../../../../../hooks/usePracticeDocuments';
import { useToast } from '../../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../../utils/apiError';
import type { PracticeDocumentType } from '../../../../../services/practiceDocuments';
import { formatDocumentSize } from './practiceDocumentFormatting';
import {
    MAX_PRACTICE_DOCUMENT_DESCRIPTION_LENGTH,
    PRACTICE_DOCUMENT_ACCEPT,
    practiceDocumentValidationError
} from './practiceDocumentUpload';

interface PracticeFileUploadFormProps {
    practiceId: string;
}

// One-file-at-a-time staging - simpler than patient-files' UploadZoneContent
// (which stages a whole batch), since this endpoint only ever accepts a
// single 'file' field per request (see files.controller.ts).
export function PracticeFileUploadForm({ practiceId }: PracticeFileUploadFormProps) {
    const { showToast } = useToast();
    const uploadDocument = useUploadPracticeDocument(practiceId);
    const [stagedFile, setStagedFile] = useState<File | null>(null);
    const [documentType, setDocumentType] = useState<PracticeDocumentType>('agreement');
    const [description, setDescription] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const stageFile = useCallback((file: File) => {
        const validationError = practiceDocumentValidationError(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setStagedFile(file);
    }, []);

    const handleDrop = useCallback(
        (e: DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) stageFile(file);
        },
        [stageFile]
    );

    const handleDragOver = useCallback((e: DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleFileInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) stageFile(file);
        },
        [stageFile]
    );

    const handleClearStaged = useCallback(() => {
        setStagedFile(null);
        setDocumentType('agreement');
        setDescription('');
        setProgress(0);
        setError(null);
    }, []);

    const handleUpload = useCallback(async () => {
        if (!stagedFile) return;
        setError(null);
        setProgress(0);
        try {
            await uploadDocument.mutateAsync({
                input: {
                    file: stagedFile,
                    documentType,
                    description: description.trim() || undefined
                },
                onProgress: setProgress
            });
            showToast('File uploaded', 'success');
            handleClearStaged();
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to upload file');
            setError(message);
            showToast(message, 'error');
        }
    }, [description, documentType, handleClearStaged, showToast, stagedFile, uploadDocument]);

    const isUploading = uploadDocument.isPending;

    return (
        <div className="flex flex-col gap-3">
            <DragAndDropZone
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onFileInputChange={handleFileInputChange}
                isDragOver={isDragOver}
                accept={PRACTICE_DOCUMENT_ACCEPT}
                showMaxSize={false}
            />
            <p className="text-[11px] text-gray-400 -mt-1.5">
                Maximum size: 25MB. PDF, JPEG, PNG, WEBP, DOC, or DOCX.
            </p>

            {stagedFile && (
                <div className="border border-gray-200 rounded-xl p-3.5 flex flex-col gap-3">
                    <div className="flex items-center gap-2.5">
                        <DocumentIcon className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-700 font-medium truncate flex-1 min-w-0">
                            {stagedFile.name}
                        </span>
                        <span className="text-xs text-gray-400 shrink-0">
                            {formatDocumentSize(stagedFile.size)}
                        </span>
                        <button
                            type="button"
                            onClick={handleClearStaged}
                            disabled={isUploading}
                            className="text-gray-400 hover:text-red-600 disabled:opacity-50 shrink-0"
                            aria-label="Remove staged file"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-gray-500">
                                Document type
                            </span>
                            <select
                                value={documentType}
                                disabled={isUploading}
                                onChange={(e) =>
                                    setDocumentType(e.target.value as PracticeDocumentType)
                                }
                                className="border border-gray-300 rounded-md h-9 px-2 text-sm text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <option value="agreement">Agreement</option>
                                <option value="other">Other</option>
                            </select>
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-gray-500">
                                Description (optional)
                            </span>
                            <input
                                type="text"
                                value={description}
                                disabled={isUploading}
                                maxLength={MAX_PRACTICE_DOCUMENT_DESCRIPTION_LENGTH}
                                onChange={(e) => setDescription(e.target.value)}
                                className="border border-gray-300 rounded-md h-9 px-2.5 text-sm text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                            />
                        </label>
                    </div>

                    {isUploading ? (
                        <div className="flex items-center gap-2.5">
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className="bg-gray-800 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <span className="text-xs text-gray-500 shrink-0">{progress}%</span>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => void handleUpload()}
                            className="self-start inline-flex items-center gap-2 bg-gray-900 text-white px-4 h-9 rounded-lg hover:bg-black transition-colors font-semibold text-sm"
                        >
                            <CloudArrowUpIcon className="h-4 w-4" />
                            Upload
                        </button>
                    )}
                </div>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
    );
}
